import type { Filesystem } from '@gitbook/openapi-parser';
import {
    OpenAPIWebhook as BaseOpenAPIWebhook,
    type OpenAPIWebhookData,
} from '@gitbook/react-openapi';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import type { DocumentJSON } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { Heading } from '@gitbook/spine-react';

import { OpenAPIBlockContent } from './OpenAPIBlockContent';
import { OpenAPIDocumentRenderer } from './OpenAPIDocumentRenderer';
import { useResolveOpenAPI } from './useResolveOpenAPI';

/**
 * Content of an OpenAPI webhook block.
 */
export function OpenAPIWebhookBlockContent(props: {
    specUrl: string;
    filesystem: Filesystem;
    name: string;
    method: string;
}) {
    const { specUrl, filesystem, name, method } = props;

    // Use name as path for webhooks
    const result = useResolveOpenAPI<OpenAPIWebhookData>({ filesystem, name, method });

    return (
        <OpenAPIBlockContent name={name} type="webhook" resolved={result}>
            {({ data }) => (
                <BaseOpenAPIWebhook
                    data={data}
                    context={{
                        locale: 'en',
                        icons: {
                            chevronDown: <Icon.ChevronDown />,
                            chevronRight: <Icon.ChevronRight />,
                            plus: <Icon.Plus />,
                        },
                        renderCodeBlock: (codeProps) => (
                            <PlainCodeBlock className="openapi-codeblock" {...codeProps} />
                        ),
                        renderHeading: ({ deprecated, title }) => (
                            <Heading className={deprecated ? 'line-through' : undefined} level={2}>
                                {title}
                            </Heading>
                        ),
                        renderDocument: ({ document }: { document: DocumentJSON }) => {
                            return <OpenAPIDocumentRenderer document={document} />;
                        },
                        defaultInteractiveOpened: false,
                        specUrl,
                    }}
                    className="openapi-block"
                />
            )}
        </OpenAPIBlockContent>
    );
}
